import axios from "axios";
import { API, getOrCreateUserId } from "./api";
import { getPublicUrl } from "./publicUrl";

/**
 * Read the current user's invite code (lazy-generated on backend) + stats.
 */
export async function fetchMyInvite() {
  const user_id = getOrCreateUserId();
  const res = await axios.get(`${API}/invites/me`, { params: { user_id } });
  return res.data; // { code, invites_redeemed, invited_by, reward_chips_inviter, tiers, next_tier, ... }
}

/**
 * Top referrers for the global leaderboard inside the Invite Friends modal.
 */
export async function fetchInviteLeaderboard(limit = 10) {
  const res = await axios.get(`${API}/invites/leaderboard`, { params: { limit } });
  return res.data.top_referrers || [];
}

/**
 * Redeem a friend's invite code. Returns { ok, chips_credited, inviter_user_id }.
 * Throws on 4xx/5xx with the server's `detail` message.
 */
export async function redeemInvite(code) {
  const user_id = getOrCreateUserId();
  const res = await axios.post(`${API}/invites/redeem`, {
    user_id,
    code: String(code || "").trim().toUpperCase(),
  });
  return res.data;
}

/**
 * Native-first share: tries Capacitor Share, falls back to navigator.share,
 * then to a clipboard copy. Returns the action that succeeded.
 */
export async function shareInvite(code) {
  const link = `${getPublicUrl()}/?invite=${encodeURIComponent(code)}`;
  const text =
    `Play Quattro with me — line up four cards to win.\n` +
    `Use my invite code ${code} for 200 free chips:\n${link}`;

  // Capacitor native share
  try {
    if (window.Capacitor?.isNativePlatform?.()) {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title: "Quattro", text, dialogTitle: "Invite a friend" });
      return "native";
    }
  } catch {}

  // Web Share API (mobile browsers + many desktops)
  try {
    if (navigator.share) {
      await navigator.share({ title: "Quattro", text });
      return "web";
    }
  } catch {}

  // Clipboard fallback
  try {
    await navigator.clipboard.writeText(text);
    return "clipboard";
  } catch {
    return "none";
  }
}
